import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { clientValidation } from '../../../utils/validationUtils';

const AddClientModal = ({ isOpen, onClose, onAddClient }) => {
  const [clientData, setClientData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    status: 'active',
    preferredLocation: '',
    budgetMin: '',
    budgetMax: '',
    propertyType: '',
    bedrooms: '',
    source: '',
    assignedAgent: '',
    priority: 'medium',
    notes: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (field, value) => {
    setClientData(prev => ({ ...prev, [field]: value }));

    // CLIENT-SIDE VALIDATION: Clear error for this field when user types
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const handleBlur = (field) => {
    // Optional: Add field-level validation on blur if needed
    // For now, this function exists to prevent undefined errors
  };

  const handleSubmit = () => {
    // CLIENT-SIDE VALIDATION: Validate before submitting
    const validation = clientValidation?.validate(clientData);
    
    if (!validation?.isValid) {
      setValidationErrors(validation?.errors);
      
      // Show error message
      const firstError = Object.values(validation?.errors)?.[0];
      alert(`خطأ في البيانات: ${firstError}`);
      return;
    }

    onAddClient?.(clientData);
    onClose();
    // Reset form
    setClientData({
      name: '',
      email: '',
      phone: '',
      category: '',
      status: 'active',
      preferredLocation: '',
      budgetMin: '',
      budgetMax: '',
      propertyType: '',
      bedrooms: '',
      source: '',
      assignedAgent: '',
      priority: 'medium',
      notes: ''
    });
    setValidationErrors({});
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="إضافة عميل جديد" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-teal-400">
            <Icon name="UserPlus" size={20} />
            <span className="font-semibold">معلومات العميل الأساسية</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">الاسم الكامل *</label>
          <Input
            placeholder="أحمد محمد"
            value={clientData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            onBlur={() => handleBlur('name')}
          />
          {validationErrors?.name && (
            <p className="text-red-400 text-xs mt-1">{validationErrors?.name}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">البريد الإلكتروني *</label>
            <Input
              type="email"
              placeholder="ahmed@example.com"
              value={clientData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              onBlur={() => handleBlur('email')}
            />
            {validationErrors?.email && (
              <p className="text-red-400 text-xs mt-1">{validationErrors?.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">رقم الهاتف *</label>
            <Input
              type="tel"
              placeholder="+962 79 123 4567"
              value={clientData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              onBlur={() => handleBlur('phone')}
            />
            {validationErrors?.phone && (
              <p className="text-red-400 text-xs mt-1">{validationErrors?.phone}</p>
            )}
          </div>
        </div>

        {validationErrors?.contact && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <p className="text-red-400 text-sm">{validationErrors?.contact}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">فئة العميل *</label>
            <Select
              value={clientData?.category}
              onChange={(e) => handleChange('category', e?.target?.value)}
            >
              <option value="">اختر الفئة</option>
              <option value="buyers">مشتري</option>
              <option value="renters">مستأجر</option>
              <option value="investors">مستثمر</option>
              <option value="vip">VIP</option>
            </Select>
            {validationErrors?.category && (
              <p className="text-red-400 text-xs mt-1">{validationErrors?.category}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">الحالة</label>
            <Select
              value={clientData?.status}
              onChange={(e) => handleChange('status', e?.target?.value)}
            >
              <option value="active">نشط</option>
              <option value="pending">قيد المتابعة</option>
              <option value="inactive">غير نشط</option>
            </Select>
          </div>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
          <div className="flex items-center gap-2 text-blue-400 mb-3">
            <Icon name="Home" size={18} />
            <span className="font-semibold text-sm">تفضيلات العقار</span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">الموقع المفضل</label>
              <Input
                placeholder="مثال: عمان، الأردن"
                value={clientData?.preferredLocation}
                onChange={(e) => handleChange('preferredLocation', e?.target?.value)}
                onBlur={() => handleBlur('preferredLocation')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">الميزانية (دينار أردني)</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    type="number"
                    placeholder="50000"
                    value={clientData?.budgetMin}
                    onChange={(e) => handleChange('budgetMin', e?.target?.value)}
                    onBlur={() => handleBlur('budgetMin')}
                  />
                  {validationErrors?.budgetMin && (
                    <p className="text-red-400 text-xs mt-1">{validationErrors?.budgetMin}</p>
                  )}
                </div>
                <div>
                  <Input
                    type="number"
                    placeholder="150000"
                    value={clientData?.budgetMax}
                    onChange={(e) => handleChange('budgetMax', e?.target?.value)}
                    onBlur={() => handleBlur('budgetMax')}
                  />
                  {validationErrors?.budgetMax && (
                    <p className="text-red-400 text-xs mt-1">{validationErrors?.budgetMax}</p>
                  )}
                </div>
              </div>
              {validationErrors?.budgetRange && (
                <p className="text-red-400 text-xs mt-1">{validationErrors?.budgetRange}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">نوع العقار</label>
                <Select
                  value={clientData?.propertyType}
                  onChange={(e) => handleChange('propertyType', e?.target?.value)}
                >
                  <option value="">اختر النوع</option>
                  <option value="apartment">شقة</option>
                  <option value="villa">فيلا</option>
                  <option value="townhouse">تاون هاوس</option>
                  <option value="penthouse">بنتهاوس</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">غرف النوم</label>
                <Select
                  value={clientData?.bedrooms}
                  onChange={(e) => handleChange('bedrooms', e?.target?.value)}
                >
                  <option value="">اختر العدد</option>
                  <option value="studio">استوديو</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5+">5+</option>
                </Select>
                {validationErrors?.bedrooms && (
                  <p className="text-red-400 text-xs mt-1">{validationErrors?.bedrooms}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">مصدر العميل</label>
            <Select
              value={clientData?.source}
              onChange={(e) => handleChange('source', e?.target?.value)}
            >
              <option value="">اختر المصدر</option>
              <option value="website">الموقع الإلكتروني</option>
              <option value="referral">إحالة</option>
              <option value="social-media">وسائل التواصل</option>
              <option value="walk-in">زيارة مباشرة</option>
              <option value="phone">مكالمة هاتفية</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">تعيين إلى</label>
            <Select
              value={clientData?.assignedAgent}
              onChange={(e) => handleChange('assignedAgent', e?.target?.value)}
            >
              <option value="">اختر الوكيل</option>
              <option value="agent-1">أحمد محمد</option>
              <option value="agent-2">سارة علي</option>
              <option value="agent-3">محمد خالد</option>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">الأولوية</label>
          <Select
            value={clientData?.priority}
            onChange={(e) => handleChange('priority', e?.target?.value)}
          >
            <option value="low">منخفضة</option>
            <option value="medium">متوسطة</option>
            <option value="high">عالية</option>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">ملاحظات</label>
          <textarea
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            rows="3"
            placeholder="أي ملاحظات أو معلومات إضافية عن العميل..."
            value={clientData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
          />
          {validationErrors?.notes && (
            <p className="text-red-400 text-xs mt-1">{validationErrors?.notes}</p>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSubmit} className="flex-1">
            <Icon name="UserPlus" size={18} className="ml-2" />
            إضافة العميل
          </Button>
          <Button onClick={onClose} variant="outline" className="flex-1">
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddClientModal;